import {
  getBusinessHours,
  updateBusinessHours,
} from '@/services/business-hours/business-hours.service';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BusinessHoursPayload } from '@/services/business-hours/business-hours.types';
import { openNotification } from '@/utils/notification/notification';
import { t } from 'i18next';

export const useBusinessHours = (company_id: string | undefined, date?: string) => {
  const time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data: businessHoursData, isLoading } = useQuery({
    queryKey: ReactQueryKeys.BUSINESS_HOURS(company_id, date),
    // Date is optional; omitting it asks the endpoint for today.
    queryFn: () => getBusinessHours({ company_id: company_id!, time_zone, date }),
    enabled: !!company_id,
  });

  return { businessHoursData, isLoading };
};

export const useUpdateBusinessHours = (company_id: string | undefined) => {
  const queryClient = useQueryClient();
  const time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {
    mutate: updateCompanyBusinessHours,
    mutateAsync: updateCompanyBusinessHoursAsync,
    isPending: isPendingUpdateBusinessHours,
  } = useMutation({
    mutationFn: (payload: BusinessHoursPayload) =>
      updateBusinessHours(payload, { company_id: company_id!, time_zone }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['business-hours'],
      });
      openNotification({
        message: t('businessHours:notifications.updated'),
        type: 'success',
      });
    },
    onError: () => {
      openNotification({
        message: t('businessHours:notifications.updateFailed'),
        type: 'error',
      });
    },
  });

  return {
    updateCompanyBusinessHours,
    updateCompanyBusinessHoursAsync,
    isPendingUpdateBusinessHours,
  };
};
